import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Plus, ArrowRight, Shield, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TopBar } from "@/components/Brand";
import { Field, H1, Overline, Pill, inputCls, Empty } from "@/components/kit";
import { api, errMsg, initials } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SPORTS } from "@/pages/Signup";

const NewTeamDialog = ({ open, onOpenChange }) => {
  const [f, setF] = useState({ team_name: "", sport: "Football", location: "" });
  const { refresh } = useAuth();
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/teams", f);
      await refresh();
      toast.success("Team created");
      nav(`/manage/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#111827]">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">New team</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Team name"><Input required value={f.team_name} onChange={(e) => setF({ ...f, team_name: e.target.value })} data-testid="new-team-name-input" /></Field>
          <Field label="Sport"><select className={inputCls} value={f.sport} onChange={(e) => setF({ ...f, sport: e.target.value })} data-testid="new-team-sport-select">{SPORTS.map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Location"><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="new-team-location-input" /></Field>
          <Button type="submit" className="w-full rounded-full font-bold" data-testid="new-team-submit-btn">Create team</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TeamCard = ({ m }) => (
  <div className="rise flex flex-col justify-between rounded-lg border border-white/[0.08] bg-[#111827] p-6" data-testid={`membership-card-${m.slug}`}>
    <div className="flex items-start gap-4">
      <span className="flex h-12 w-12 items-center justify-center rounded-md font-display text-lg font-black text-[#0B0F17]" style={{ background: m.primary_color }}>{initials(m.team_name)}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-2xl font-bold uppercase text-white">{m.team_name}</p>
        <div className="mt-1 flex flex-wrap gap-2">
          <Pill tone={m.role === "admin" ? "volt" : "cyan"}>{m.role === "admin" ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}{m.role}</Pill>
          {m.status === "paused" && <Pill tone="red">Paused</Pill>}
        </div>
      </div>
    </div>
    <div className="mt-6 flex flex-wrap gap-2">
      {m.role === "admin" && (
        <Button asChild size="sm" className="rounded-full font-bold" data-testid={`manage-team-${m.slug}`}><Link to={`/manage/${m.team_id}`}>Manage <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
      )}
      <Button asChild size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid={`player-app-${m.slug}`}><Link to={`/t/${m.slug}/players`}>Player app</Link></Button>
      <Button asChild size="sm" variant="ghost" className="rounded-full" data-testid={`fan-hub-${m.slug}`}><Link to={`/t/${m.slug}`}>Fan hub</Link></Button>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user.platform_role === "super_admin" && !user.memberships.length) return <Navigate to="/admin" replace />;
  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <TopBar>{user.platform_role === "super_admin" && <Link to="/admin" className="text-sm text-[#CCFF00]" data-testid="nav-admin-link">Platform console</Link>}</TopBar>
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-8">
        <Overline>Hi {user.name || "there"}</Overline>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <H1>Your teams</H1>
          <Button onClick={() => setOpen(true)} className="rounded-full font-bold" data-testid="create-team-btn"><Plus className="mr-1 h-4 w-4" /> New team</Button>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {user.memberships.map((m) => <TeamCard key={m.member_id} m={m} />)}
        </div>
        {!user.memberships.length && <Empty title="No teams yet" text="Create a team to get started, or ask your team admin to invite you." />}
      </main>
      <NewTeamDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
